import React, { useEffect } from 'react';
import QRCode from 'qrcode.react';
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useOnboarding } from '../../../contexts/onboarding.context';
import useActiveTheme from '../../../hooks/activeTheme.hook';
import useAppInfo from '../../../hooks/appInfo.hook';
import usePreviewerDetection from '../../../hooks/previewerDetection.hook';
import { mobilizeiDevicesService } from '../../../services/mobilizei/devices.service';
import { showApiError } from '../../../utils/errors';
import { baseDeeplink, mixpanelEvents } from '../../../utils/constants';
import OnbFrame from '../_shared/OnbFrame';
import PhoneFrame from '../_shared/PhoneFrame';
import MockAppHome, { useMockAppPreview } from '../_shared/MockAppHome';
import ReturnCallout from '../_shared/ReturnCallout';
import { COLORS } from '../_shared/tokens';
import appStoreBadge from '../../../assets/img/download_app_store.png';
import googlePlayBadge from '../../../assets/img/download_google_play.png';
import visualizadorIcon from '../../../assets/img/previewer_icon_ios.png';
import mixpanel from 'mixpanel-browser';
import { toast } from 'react-toastify';

// Same deep link shape the panel uses elsewhere (see pages/Dashboard/Preview).
// The ids matter beyond the download: AppsFlyer hands them to the previewer, which
// is what makes it open this store — and therefore what triggers the detection.
const buildAppLink = (platformId, storeId) =>
    `${baseDeeplink}&deep_link_value=${platformId}&deep_link_sub1=${storeId}`;

// The connected-device label depends on the OS: iOS devices report a usable
// model name, Android ones do not. The os field can come with mixed casing.
const buildDeviceLabel = (device) => {
    const os = device?.os?.toLowerCase();

    if (os === 'ios' && device?.modelName) {
        return `${device.modelName} conectado`;
    }

    if (os === 'android') {
        return 'celular Android conectado';
    }

    return 'celular conectado';
};

// Desktop scans a QR; mobile installs on the device it is already running on.
const DESKTOP_STEPS = [
    {
        title: 'Escaneie o QR Code',
        text: 'Aponte a câmera do seu celular para o QR ao lado.'
    },
    {
        title: 'Instale o Visualizador',
        text: 'O QR abre a loja de aplicativos do seu celular. Instale o Visualizador Mobilizei por lá.'
    },
    {
        title: 'Abra e explore seu app',
        text: 'Abra o Visualizador e explore sua loja. Depois, volte aqui para enviar sua primeira notificação.'
    }
];

const MOBILE_STEPS = [
    'Instale o Visualizador Mobilizei.',
    'Abra o app e explore sua loja.'
];

function WaitingText({ children }) {
    const [dots, setDots] = React.useState(0);

    React.useEffect(() => {
        const id = setInterval(() => setDots((d) => (d + 1) % 4), 450);
        return () => clearInterval(id);
    }, []);

    return (
        <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
            {children}
            <Box component="span" aria-hidden sx={{ display: 'inline-block', width: '1.1em', textAlign: 'left' }}>
                {'.'.repeat(dots)}
            </Box>
        </Box>
    );
}

function NumberedStep({ index, children, size = 28 }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
                sx={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    backgroundColor: COLORS.green,
                    color: '#fff',
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: size >= 28 ? 14 : 13
                }}
            >
                {index}
            </Box>
            <Typography sx={{ fontSize: { xs: 15, md: 16 }, lineHeight: 1.4, pt: 0.375 }}>{children}</Typography>
        </Box>
    );
}

function VisualizadorRow({ compact, appLink }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                ...(compact ? {} : { p: '14px 16px', backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: '8px' })
            }}
        >
            <Box component="img" src={visualizadorIcon} alt="" sx={{ width: compact ? 38 : 44, height: compact ? 38 : 44, borderRadius: '10px' }} />

            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: compact ? 14 : 15 }}>Visualizador Mobilizei</Typography>
                <Typography sx={{ fontSize: compact ? 12.5 : 13, color: COLORS.textMuted }}>iOS · Android · grátis</Typography>
            </Box>

            {!compact && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                        component="a"
                        href="https://apps.apple.com/br/app/mobilizei-visualizador/id1568180845"
                        target="_blank"
                        rel="noreferrer"
                        sx={{ display: 'flex' }}
                    >
                        <Box component="img" src={appStoreBadge} alt="App Store" sx={{ height: 30, display: 'block' }} />
                    </Box>
                    <Box
                        component="a"
                        href="https://play.google.com/store/apps/details?id=br.com.mobilizei.previewer"
                        target="_blank"
                        rel="noreferrer"
                        sx={{ display: 'flex' }}
                    >
                        <Box component="img" src={googlePlayBadge} alt="Google Play" sx={{ height: 30, display: 'block' }} />
                    </Box>
                </Box>
            )}
        </Box>
    );
}

/**
 * D03 · Ver no celular (step 5 of 7).
 * Desktop shows a QR code; mobile replaces it with a direct download plus the
 * ReturnCallout, since the user leaves the browser to install and coming back
 * is the most important instruction. Advances to D04 only once `detected`.
 */
export default function Preview() {
    const { goToNextStep, screenState, setScreenState, data } = useOnboarding();
    const { appInfo } = useAppInfo();
    const { homeScreenSections, themeSettings } = useActiveTheme();
    const { isHidden: isMockHidden } = useMockAppPreview();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const isDetected = screenState === 'detected';

    // 'paused' is where the delay ends: the polling gave up, so the screen says so
    // and offers to start over. Everything the delay state opened up (the email
    // fallback, the way out of the step) still applies here.
    const isPaused = screenState === 'paused';
    const isDelay = screenState === 'delay' || isPaused;

    const appLink = buildAppLink(appInfo.platformId, appInfo.storeId);

    // The detected device (when it comes from real detection). Stays null on the
    // manual "já abri o app" path, so the UI must fall back gracefully.
    const [device, setDevice] = React.useState(null);

    const [isSendingEmail, setIsSendingEmail] = React.useState(false);
    const [emailSent, setEmailSent] = React.useState(false);

    useEffect(() => {
        mixpanel.track(mixpanelEvents.ONBOARDING_PREVIEWER_DOWNLOAD_SCREEN_SHOWN, {
            'platform_id': appInfo.platformId,
            'store_id': appInfo.storeId
        })
    }, [])

    usePreviewerDetection({
        platformId: appInfo.platformId,
        storeId: appInfo.storeId,
        enabled: !isDetected && !isPaused,
        onDetected: (detectedDevice) => {
            setDevice(detectedDevice);
            setScreenState('detected');
        },
        // Not a failure: the merchant may just be slow to install. The screen only
        // softens into its "still waiting" variant and keeps the manual way out.
        onTimeout: () => {
            if (isDesktop) {
                mixpanel.track(mixpanelEvents.ONBOARDING_PREVIEWER_QR_SCAN_DELAYED, {
                    'platform_id': appInfo.platformId,
                    'store_id': appInfo.storeId
                })
            }
            setScreenState('delay')
        },
        onStopped: () => {
            setScreenState('paused')
        }
    });

    // Back to 'waiting' re-enables the hook, and a fresh run means a fresh clock:
    // the delay copy can come back before it gives up again.
    function handleResumeDetection() {
        setScreenState('waiting')
    }

    // The merchant who cannot scan the QR receives the very same link by email, on
    // the address of his account — there is nothing to type here.
    async function handleSendDownloadLink() {
        setIsSendingEmail(true);

        try {
            await mobilizeiDevicesService.sendPreviewerDownloadLink(appInfo.platformId, appInfo.storeId);

            setEmailSent(true);
            toast.success('O e-mail foi enviado com sucesso!');
        } catch (e) {
            showApiError(e?.error);
        } finally {
            setIsSendingEmail(false);
        }
    }

    function handleGoToNextStep() {
        goToNextStep()
        mixpanel.track(mixpanelEvents.ONBOARDING_GO_TO_NOTIFICATION_CLICKED, {
            'platform_id': appInfo.platformId,
            'store_id': appInfo.storeId
        })
    }

    function previewerDownloadSkipped() {
        goToNextStep()
        mixpanel.track(mixpanelEvents.ONBOARDING_PREVIEWER_DOWNLOAD_SKIPPED, {
            'platform_id': appInfo.platformId,
            'store_id': appInfo.storeId
        })
    }

    // ── detected: celebratory bridge ──
    // The download did its job, so it collapses into a discreet line and a
    // single dominant CTA points at the push.
    if (isDetected) {
        return (
            <OnbFrame step={5}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: isMockHidden ? '1fr' : '1.08fr 1fr' },
                        gap: { xs: 4, md: 8 },
                        alignItems: 'center'
                    }}
                >
                    <Box sx={{ maxWidth: 560 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: '999px',
                                backgroundColor: 'rgba(60,213,104,0.1)',
                                color: COLORS.forest,
                                fontWeight: 700,
                                fontSize: 13,
                                mb: 3.75
                            }}
                        >
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: COLORS.green }} />
                            Visualizador instalado
                        </Box>

                        <Typography
                            component="h1"
                            sx={{ fontWeight: 700, fontSize: { xs: 32, md: 50 }, lineHeight: 1.05, letterSpacing: '-1.2px', mb: 2.25 }}
                        >
                            {isDesktop ? (
                                <>Sua loja está<br /><Box component="span" sx={{ color: COLORS.green }}>no seu celular!</Box></>
                            ) : (
                                <>Sua loja está<br /><Box component="span" sx={{ color: COLORS.green }}>ao vivo no Visualizador.</Box></>
                            )}
                        </Typography>

                        <Typography sx={{ fontSize: { xs: 16, md: 18 }, lineHeight: 1.55, color: COLORS.textMuted, mb: 4.5, maxWidth: 480 }}>
                            Explore o app à vontade. Quando quiser, siga para o próximo passo para enviar sua primeira notificação.
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={handleGoToNextStep}
                            endIcon={<ArrowForwardIcon />}
                            fullWidth={!isDesktop}
                            sx={{ px: 5, py: 2.25, fontSize: 18, borderRadius: '12px', boxShadow: '0 12px 30px rgba(60,213,104,0.35)' }}
                        >
                            ir para a notificação
                        </Button>

                        <Typography
                            sx={{
                                mt: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'center', md: 'flex-start' },
                                gap: 1,
                                fontSize: 14,
                                color: COLORS.textMuted
                            }}
                        >
                            <LockOpenIcon sx={{ fontSize: 16 }} />
                            Nada é enviado agora, você dispara quando estiver pronto.
                        </Typography>
                    </Box>

                    {!isMockHidden && (
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <Box
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    width: 480,
                                    height: 480,
                                    background: 'radial-gradient(circle, rgba(60,213,104,0.22), transparent 70%)',
                                    borderRadius: '50%'
                                }}
                            />

                            <PhoneFrame scale={isDesktop ? 0.92 : 0.5} sx={{ position: 'relative' }}>
                                <MockAppHome
                                    homeScreenSections={homeScreenSections}
                                    primaryColor={data.primaryColor || '#000'}
                                    storeName={data.storeName}
                                    themeSettings={themeSettings}
                                />
                            </PhoneFrame>

                            <Box
                                sx={{
                                    position: 'absolute',
                                    zIndex: 3,
                                    left: '50%',
                                    ml: '135px',
                                    top: '50%',
                                    transform: 'translateY(-140px)',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: '999px',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 10px 26px rgba(0,0,0,0.16)',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: COLORS.forest
                                }}
                            >
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: COLORS.green, boxShadow: '0 0 0 4px rgba(60,213,104,0.25)' }} />
                                {buildDeviceLabel(device)}
                            </Box>
                        </Box>
                    )}
                </Box>
            </OnbFrame>
        );
    }

    // ── mobile waiting: no QR, direct download, and "come back here" ──
    if (!isDesktop) {
        return (
            <OnbFrame step={5}>
                <Box
                    sx={{
                        alignSelf: 'flex-start',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '999px',
                        backgroundColor: COLORS.forest,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 12,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        mt: 1.5,
                        mb: 2.25
                    }}
                >
                    <DownloadIcon sx={{ fontSize: 13 }} />
                    baixe o Visualizador
                </Box>

                <Typography component="h1" sx={{ fontWeight: 700, fontSize: 28, lineHeight: 1.15, letterSpacing: '-0.5px', mb: 1.5 }}>
                    Baixe o Visualizador<br />para ver seu app.
                </Typography>

                <Typography sx={{ fontSize: 16, lineHeight: 1.5, color: COLORS.textMuted, mb: 2.75 }}>
                    O <Box component="b" sx={{ color: '#000' }}>Visualizador Mobilizei</Box> é o app que carrega a {data.storeName} para você testar tudo na prática. Clique no botão abaixo para ir direto para a loja de aplicativos do seu celular.
                </Typography>

                {/* component="a" keeps MUI from routing this through LinkBehavior
                    (see theme.js): the deep link must leave the SPA for the store. */}
                <Button
                    fullWidth
                    variant="contained"
                    component="a"
                    href={appLink}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<GetAppIcon sx={{ fontSize: 22 }} />}
                    sx={{ py: 2.25, fontSize: 17, borderRadius: '12px', boxShadow: '0 10px 26px rgba(60,213,104,0.4)' }}
                    onClick={() => {
                        mixpanel.track(mixpanelEvents.ONBOARDING_PREVIEWER_LINK_CLICKED, {
                            'platform_id': appInfo.platformId,
                            'store_id': appInfo.storeId
                        })
                    }}
                >
                    baixar o Visualizador
                </Button>

                <Typography
                    sx={{ mt: 1.25, mb: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, fontSize: 13, color: COLORS.textMuted }}
                >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                    abre a App Store ou o Google Play do seu celular
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.25 }}>
                    {MOBILE_STEPS.map((text, i) => (
                        <NumberedStep key={text} index={i + 1} size={24}>{text}</NumberedStep>
                    ))}
                </Box>

                {/* Step 3 — the return, with its own heavy treatment */}
                <Box sx={{ mb: 2.75 }}>
                    <ReturnCallout />
                </Box>

                {/* The page keeps waiting for them to come back — until it gives up
                    and asks whether it should look again. */}
                {isPaused ? (
                    <Box sx={{ p: '13px 15px', backgroundColor: COLORS.warningBg, borderRadius: '12px', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PauseCircleOutlineIcon sx={{ fontSize: 17, flex: 'none', color: COLORS.warningFg }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.35, color: COLORS.warningFg }}>
                                Busca pausada
                            </Typography>
                        </Box>

                        <Typography sx={{ mt: 0.75, fontSize: 14, lineHeight: 1.45, color: COLORS.textMuted }}>
                            Paramos de procurar depois de alguns minutos sem resposta. Quando estiver com o
                            Visualizador instalado, é só pedir para procurarmos de novo.
                        </Typography>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleResumeDetection}
                            startIcon={<RefreshIcon />}
                            sx={{ mt: 1.75, py: 1.5, fontSize: { xs: 14, md: 16 }, borderRadius: '12px' }}
                        >
                            procurar meu celular de novo
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '13px 15px', backgroundColor: 'rgba(60,213,104,0.08)', borderRadius: '12px', mb: 2 }}>
                        <CircularProgress size={15} thickness={5} sx={{ flex: 'none' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.35, color: COLORS.forest }}>
                            Estamos esperando você baixar o Visualizador.
                        </Typography>
                    </Box>
                )}

                <VisualizadorRow compact appLink={appLink} />

                <Typography sx={{ mt: 2.5, fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted }}>
                    <Box component="b">Já tem o Visualizador?</Box> Toque no botão acima para abrir o app e carregar sua loja.
                </Typography>

                {isDelay && (
                    <>
                        {/* Separates the way out from the instructions above it, which on
                        mobile run to the bottom of the screen with nothing between them. */}
                        <Box sx={{ mt: 2.5, height: '1px', backgroundColor: COLORS.divider }} />

                        <Typography sx={{ mt: 2.5, fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted }}>
                            <Box
                                component="button"
                                type="button"
                                onClick={previewerDownloadSkipped}
                                sx={{
                                    p: 0,
                                    border: 0,
                                    background: 'none',
                                    font: 'inherit',
                                    color: '#000',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                            >
                                Continuar para o próximo passo
                            </Box>
                            {' '}• você pode baixar o Visualizador mais tarde.
                        </Typography>
                    </>
                )}
            </OnbFrame>
        );
    }

    // ── desktop waiting / delay: QR ──
    return (
        <OnbFrame step={5}>
            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7.5, alignItems: 'center' }}>
                <Box sx={{ maxWidth: 540, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.75,
                            py: 0.75,
                            borderRadius: '999px',
                            backgroundColor: COLORS.forest,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 13,
                            letterSpacing: '0.6px',
                            textTransform: 'uppercase',
                            mb: 3
                        }}
                    >
                        <SmartphoneOutlinedIcon sx={{ fontSize: 14 }} />
                        é hora de pegar seu celular
                    </Box>

                    <Typography component="h1" sx={{ fontWeight: 700, fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.8px', mb: 2 }}>
                        Veja seu app<br />no seu próprio celular.
                    </Typography>

                    <Typography sx={{ fontSize: 18, lineHeight: 1.55, color: COLORS.textMuted, mb: 3.5, maxWidth: 460 }}>
                        Baixe o <Box component="b" sx={{ color: '#000' }}>Visualizador Mobilizei</Box>. Ele carrega a{' '}
                        {data.storeName} para você testar tudo na prática.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mb: 3.5 }}>
                        {DESKTOP_STEPS.map((step, i) => (
                            <NumberedStep key={step.title} index={i + 1}>
                                <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>{step.title}</Box>
                                {step.text}
                            </NumberedStep>
                        ))}
                    </Box>

                    <VisualizadorRow appLink={appLink} />

                    {isDelay ? (
                        <Box
                            sx={{
                                mt: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.75,
                                p: '14px 16px',
                                backgroundColor: '#FFFDF2',
                                border: '1px solid rgba(116,66,16,0.25)',
                                borderRadius: '12px'
                            }}
                        >
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: '50%',
                                    flex: 'none',
                                    backgroundColor: COLORS.warningBg,
                                    color: COLORS.warningFg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <HelpOutlineIcon sx={{ fontSize: 19 }} />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>Com dificuldade para escanear?</Typography>
                                <Typography sx={{ fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted, mt: 0.25 }}>
                                    Aponte a câmera diretamente para o QR ou receba o link por e-mail.
                                </Typography>
                            </Box>

                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                disabled={isSendingEmail}
                                onClick={handleSendDownloadLink}
                                startIcon={
                                    isSendingEmail ? <CircularProgress size={15} color="inherit" />
                                        : emailSent ? <CheckCircleOutlineIcon />
                                            : <LinkIcon />
                                }
                                sx={{ flex: 'none', textTransform: 'none' }}
                            >
                                {isSendingEmail ? 'enviando...' : emailSent ? 'link enviado' : 'enviar link por e-mail'}
                            </Button>
                        </Box>
                    ) : (
                        <Typography sx={{ mt: 3, fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted }}>
                            <b>Já tem o Visualizador?</b> Abra o app e escaneie o QR code para carregar sua loja.
                        </Typography>
                    )}

                    {isDelay && (
                        <Typography sx={{ mt: 2, fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted }}>
                            <Box
                                component="button"
                                type="button"
                                onClick={previewerDownloadSkipped}
                                sx={{
                                    p: 0,
                                    border: 0,
                                    background: 'none',
                                    font: 'inherit',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                            >
                                Continuar sem o celular
                            </Box>
                            {' '}• você pode baixar o Visualizador mais tarde.
                        </Typography>
                    )}
                </Box>

                {/* QR card */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            width: 460,
                            height: 460,
                            background: 'radial-gradient(circle, rgba(60,213,104,0.22), transparent 70%)',
                            borderRadius: '50%'
                        }}
                    />

                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '22px',
                            p: 5,
                            position: 'relative',
                            zIndex: 2,
                            boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: '1.2px', textTransform: 'uppercase', color: COLORS.textMuted }}>
                            aponte a câmera
                        </Typography>

                        <QRCode value={appLink} size={300} />

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                px: 2.25,
                                py: 1.25,
                                borderRadius: '999px',
                                backgroundColor: isDelay ? COLORS.warningBg : 'rgba(60,213,104,0.1)',
                                color: isDelay ? COLORS.warningFg : COLORS.forest,
                                fontWeight: 700,
                                fontSize: 14
                            }}
                        >
                            {isPaused ? (
                                <>
                                    <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    busca pausada
                                </>
                            ) : isDelay ? (
                                <>
                                    <HourglassEmptyIcon sx={{ fontSize: 16 }} />
                                    <WaitingText>ainda aguardando</WaitingText>
                                </>
                            ) : (
                                <>
                                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    <WaitingText>aguardando seu celular</WaitingText>
                                </>
                            )}
                        </Box>

                        {/* The QR stays valid while the search sleeps — the merchant can
                            still scan it; this only puts the detection back to work. */}
                        {isPaused && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, mt: -1 }}>
                                <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: COLORS.textMuted, textAlign: 'center', maxWidth: 340 }}>
                                    Paramos de procurar depois de alguns minutos sem resposta. Quando você estiver
                                    com o celular em mãos, é só pedir para procurarmos de novo.
                                </Typography>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleResumeDetection}
                                    startIcon={<RefreshIcon />}
                                    sx={{ py: 1.75, fontSize: 16, borderRadius: '12px', boxShadow: '0 10px 26px rgba(60,213,104,0.35)' }}
                                >
                                    procurar meu celular de novo
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </OnbFrame>
    );
}
